import { forwardRef, type TextareaHTMLAttributes } from 'react';
import { Field } from '@/components/ui/Field';
import { describedByIds, fieldControlClass } from '@/components/ui/fieldClasses';

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  hint?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, label, error, hint, id, required, 'aria-describedby': describedBy, ...props },
  ref,
) {
  const inputId = id ?? props.name;
  const hintId = inputId ? `${inputId}-hint` : undefined;
  const errorId = inputId ? `${inputId}-error` : undefined;

  const control = (
    <textarea
      id={inputId}
      ref={ref}
      required={required}
      className={fieldControlClass({ error: Boolean(error), className })}
      aria-invalid={Boolean(error) || undefined}
      aria-describedby={
        inputId
          ? describedByIds({
              id: inputId,
              error: Boolean(error),
              hint: Boolean(hint),
              extra: describedBy,
            })
          : describedBy
      }
      {...props}
    />
  );

  if (!label && !error && !hint) return control;

  return (
    <Field
      label={label}
      htmlFor={inputId}
      required={required}
      hint={hint}
      error={error}
      hintId={hintId}
      errorId={errorId}
    >
      {control}
    </Field>
  );
});
